import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight, Search, Undo, Redo, ChevronDown, Star, Image, Link, Check, Info, Upload, CloudUpload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

const IspaiConnectDemo = () => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isConnectOpen, setIsConnectOpen] = useState<boolean>(false);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedSolution, setSelectedSolution] = useState<string | null>(null);
  const [showChannelDropdown, setShowChannelDropdown] = useState<boolean>(false);
  const [showSolutionDropdown, setShowSolutionDropdown] = useState<boolean>(false);
  const [syncOption, setSyncOption] = useState<string>("daily");

  const steps = [
    {
      id: "welcome",
      title: "Let's get started and connect your first document to your iSPAI profile",
      description: "Your Giblet™ will continually learn and train itself from this content as you make updates"
    },
    {
      id: "channel-solution",
      title: "New connection",
      documentName: "Autonomous Equipment Safety Protocols"
    },
    {
      id: "channel-select",
      title: "Choose a channel"
    },
    {
      id: "link-document",
      title: "New connection",
      documentName: "Autonomous Equipment Safety Protocols"
    },
    {
      id: "solution-select",
      title: "Choose a solution"
    },
    {
      id: "sync-settings",
      title: "New connection",
      documentName: "Autonomous Equipment Safety Protocols"
    },
    {
      id: "main-menu",
      title: "iSPAI Connect",
      documentName: "Autonomous Equipment Safety..."
    },
    {
      id: "success",
      title: "Success!",
      description: "Your document was connected"
    }
  ];

  const channels = [
    { id: "giblet", name: "Giblet CONSTRUCT", logo: "/lovable-uploads/89fabeac-4ad0-47f7-94d8-1ebe79838311.png" },
    { id: "bechtel", name: "Build the future", logo: "/lovable-uploads/4868c21b-4b17-45a1-b9ea-b06f47744e63.png" },
    { id: "chevron", name: "Safe Work Practice Awareness", logo: "/lovable-uploads/bd253b3b-d99e-4630-b5a8-0e4b61f0df54.png" },
    { id: "giblet-new", name: "Channel title", logo: "/lovable-uploads/89fabeac-4ad0-47f7-94d8-1ebe79838311.png", isNew: true },
    { id: "oil-gas", name: "EHS Standards", logo: "/lovable-uploads/8a556e19-9fef-436f-b2a4-9b96b85aaec9.png" }
  ];

  const solutions = [
    { id: "construction-safety", name: "Construction Safety Blueprint Modernization", isNew: true },
    { id: "autonomous-safety", name: "Autonomous Equipment Safety Protocols", rating: 5, isNew: true }
  ];

  const handleOpenExtension = () => {
    setIsConnectOpen(true);
    setCurrentStep(0);
  };

  const handleCloseExtension = () => {
    setIsConnectOpen(false);
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      
      setShowChannelDropdown(false);
      setShowSolutionDropdown(false);
      
      if (currentStep === 1) {
        setSelectedChannel("giblet");
      } else if (currentStep === 3) {
        setSelectedSolution("autonomous-safety");
      }
    } else {
      setCurrentStep(0);
    }
  };

  const selectChannel = (channelId: string) => {
    setSelectedChannel(channelId);
    setShowChannelDropdown(false);
    handleNextStep();
  };

  const selectSolution = (solutionId: string) => {
    setSelectedSolution(solutionId);
    setShowSolutionDropdown(false);
    handleNextStep();
  };

  const getSelectedChannel = () => {
    return channels.find(channel => channel.id === selectedChannel);
  };

  const getSelectedSolution = () => {
    return solutions.find(solution => solution.id === selectedSolution);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isConnectOpen && !target.closest(".ispai-connect-panel")) {
        if (!target.closest(".ispai-connect-toggle")) {
          handleCloseExtension();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isConnectOpen]);

  return (
    <div className="min-h-screen bg-white flex flex-col mx-auto overflow-hidden" style={{ maxWidth: "1200px" }}>
      <div className="bg-[#f1f1f1] border-b border-gray-300 px-4 py-2">
        <div className="flex items-center space-x-3">
          <div className="flex space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="flex items-center bg-[#d8d8d8] rounded-t-lg px-3 py-1 max-w-md">
            <div className="flex items-center overflow-hidden">
              <span className="text-xs text-gray-800 truncate">Autonomous Equipment Safety Protocols</span>
            </div>
            <button className="ml-2 text-gray-600 hover:text-gray-800">
              <X size={14} />
            </button>
          </div>
          <button className="text-gray-600">
            <span className="text-xl">+</span>
          </button>
        </div>
      </div>
      
      <div className="bg-[#f1f1f1] flex items-center px-3 py-2 border-b border-gray-300">
        <div className="flex items-center space-x-3">
          <button className="text-gray-600 hover:bg-gray-200 p-1 rounded-full" onClick={() => navigate("/engage")}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <button className="text-gray-600 hover:bg-gray-200 p-1 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
          <button className="text-gray-600 hover:bg-gray-200 p-1 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
          </button>
          <button className="text-gray-600 hover:bg-gray-200 p-1 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </button>
        </div>
        
        <div className="flex-grow mx-2">
          <div className="bg-white border border-gray-300 rounded-full flex items-center px-3 py-1">
            <div className="flex-shrink-0 text-gray-500 mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </div>
            <div className="flex-grow">
              <span className="text-xs text-gray-600">docs.google.com/document/d/1KxMP_-IHoPrxT-OL0mZXev3uEgjKn5AmKr7K</span>
            </div>
            <div className="flex-shrink-0 text-gray-500 ml-2">
              <Star size={16} />
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button className="text-gray-600 hover:bg-gray-200 p-1 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h-.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0 .33 1.82V9c.26.85 1.08 1.39 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </button>
          <button className="text-gray-600 hover:bg-gray-200 p-1 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          </button>
          <button className="text-gray-600 hover:bg-gray-200 p-1 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M16 12h2"/><path d="M9 12H4"/><path d="M12 16V8"/></svg>
          </button>
          
          <button 
            onClick={handleOpenExtension}
            className="text-gray-500 hover:bg-gray-200 p-2 rounded-full relative ispai-connect-toggle"
          >
            <img 
              src="/lovable-uploads/100c1cb9-8584-4a8a-b73c-4e2586b902a9.png" 
              alt="iSPAI" 
              className="h-6 w-6" 
            />
          </button>
          
          <img 
            src="/lovable-uploads/384d027d-e850-413a-85b8-c9664d5da18c.png" 
            alt="User" 
            className="w-8 h-8 rounded-full"
          />
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 py-3 px-4">
        <div className="flex items-center">
          <div className="flex items-center space-x-2">
            <div className="bg-blue-500 text-white p-2 rounded">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5a2 2 0 0 0-2-2h-2"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            </div>
            <div>
              <h1 className="text-lg font-normal">Autonomous Equipment Safety Protocols</h1>
              <div className="flex space-x-6 text-sm text-gray-600 mt-1">
                <button className="hover:bg-gray-100 px-1 rounded">File</button>
                <button className="hover:bg-gray-100 px-1 rounded">Edit</button>
                <button className="hover:bg-gray-100 px-1 rounded">View</button>
                <button className="hover:bg-gray-100 px-1 rounded">Insert</button>
                <button className="hover:bg-gray-100 px-1 rounded">Format</button>
                <button className="hover:bg-gray-100 px-1 rounded">Tools</button>
                <button className="hover:bg-gray-100 px-1 rounded">Extensions</button>
                <button className="hover:bg-gray-100 px-1 rounded">Help</button>
              </div>
            </div>
          </div>

          <div className="flex-grow"></div>

          <div className="flex items-center space-x-4">
            <button className="text-gray-600 hover:bg-gray-100 p-2 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </button>
            <button className="text-gray-600 hover:bg-gray-100 p-2 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </button>
            <button className="text-gray-600 hover:bg-gray-100 p-2 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5Z"/></svg>
            </button>
            <div className="flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-md border border-blue-200">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><rect x="18" y="3" width="4" height="18"></rect><rect x="10" y="8" width="4" height="13"></rect><rect x="2" y="13" width="4" height="8"></rect></svg>
              <span className="font-medium">Share</span>
              <ChevronDown size={16} className="ml-1" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center space-x-3 overflow-x-auto">
        <Search size={18} className="text-gray-500" />
        <Undo size={18} className="text-gray-500" />
        <Redo size={18} className="text-gray-500" />
        <button className="text-gray-600 hover:bg-gray-100 p-2 rounded">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        </button>
        <div className="flex items-center border-r border-gray-300 pr-3">
          <span className="text-gray-700 text-sm">100%</span>
          <ChevronDown size={14} className="text-gray-500 ml-1" />
        </div>
        
        <div className="flex items-center border-r border-gray-300 pr-3">
          <span className="text-gray-700 text-sm">Title</span>
          <ChevronDown size={14} className="text-gray-500 ml-1" />
        </div>
        
        <div className="flex items-center border-r border-gray-300 pr-3">
          <span className="text-gray-700 text-sm">Arial</span>
          <ChevronDown size={14} className="text-gray-500 ml-1" />
        </div>
        
        <div className="flex items-center space-x-1">
          <button className="text-gray-600 hover:bg-gray-100 p-1 rounded">-</button>
          <span className="text-gray-700 text-sm px-2 border border-gray-300 rounded">26</span>
          <button className="text-gray-600 hover:bg-gray-100 p-1 rounded">+</button>
        </div>
        
        <div className="flex items-center space-x-2 border-r border-gray-300 pr-3">
          <button className="text-gray-600 hover:bg-gray-100 p-1 rounded font-bold">B</button>
          <button className="text-gray-600 hover:bg-gray-100 p-1 rounded italic">I</button>
          <button className="text-gray-600 hover:bg-gray-100 p-1 rounded underline">U</button>
          <button className="text-gray-600 hover:bg-gray-100 p-1 rounded">A</button>
          <button className="text-gray-600 hover:bg-gray-100 p-1 rounded">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>
          </button>
        </div>
        
        <Link size={18} className="text-gray-500" />
        <Image size={18} className="text-gray-500" />
        
        <button className="text-gray-600 hover:bg-gray-100 p-1 rounded">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
        </button>
        
        <div className="ml-auto">
          <button className="text-gray-600 hover:bg-gray-100 p-1 rounded">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/><path d="m21 16-4 4-4-4"/><path d="M17 20V4"/></svg>
          </button>
        </div>
      </div>

      <div className="flex flex-grow overflow-hidden">
        <div className="bg-gray-100 w-8 flex-shrink-0 flex flex-col items-center pt-4">
          <button className="text-gray-500 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
          </button>
        </div>
        
        <div className="flex-grow overflow-auto p-6 bg-gray-100">
          <div className="max-w-3xl mx-auto bg-white min-h-[calc(100vh-220px)] shadow-md p-12">
            <h1 className="text-4xl font-normal mb-6 relative">
              Autonomous Equipment Safety Protocols
              <span className="absolute h-4 w-0.5 bg-black animate-pulse ml-1"></span>
            </h1>
            
            <h2 className="text-xl font-medium mt-10 mb-3">Introduction</h2>
            <p className="text-base mb-4">
              The integration of autonomous equipment in construction, mining, and industrial operations has 
              significantly enhanced productivity, efficiency, and safety. However, the deployment of 
              autonomous machinery introduces new risks and challenges that require robust safety protocols 
              to prevent accidents, mitigate operational hazards, and ensure regulatory compliance.
            </p>
            
            <h2 className="text-xl font-medium mt-8 mb-3">1. Risk Assessment and Hazard Identification</h2>
            <p className="text-base mb-4">
              Before deploying autonomous equipment, organizations must conduct comprehensive risk 
              assessments to identify potential hazards, such as:
            </p>
            <ul className="list-disc pl-8 space-y-2 mb-4">
              <li>
                <span className="font-semibold">Human-Machine Interaction Risks:</span> Unexpected worker presence in autonomous 
                equipment operating zones.
              </li>
              <li>
                <span className="font-semibold">Environmental Hazards:</span> Uneven terrain, weather conditions, and site-specific 
                obstacles that may impact sensor accuracy.
              </li>
              <li>
                <span className="font-semibold">System Malfunctions:</span> Potential software glitches, hardware failures, or cybersecurity 
                vulnerabilities that could compromise safe operation.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {isConnectOpen && (
        <div className="fixed top-0 right-0 z-50" style={{ right: "calc(50% - 600px)" }}>
          <div className="bg-[#481d88] text-white w-[320px] h-screen max-h-[460px] rounded-lg overflow-hidden shadow-xl ispai-connect-panel">
            <div className="flex justify-between items-center p-2 border-b border-purple-700">
              <h3 className="font-medium text-white text-sm">
                <span>Giblet</span> Connect
              </h3>
              <button 
                onClick={handleCloseExtension}
                className="text-white hover:text-gray-200"
              >
                <X size={16} />
              </button>
            </div>

            <div className="bg-[#0a0b16] p-3 h-[calc(100%-40px)] flex flex-col overflow-y-auto">
              {currentStep === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-3">
                  <img src="/lovable-uploads/100c1cb9-8584-4a8a-b73c-4e2586b902a9.png" alt="Owl" className="w-12 h-12 mb-2" />
                  <h3 className="text-lg font-light leading-tight">{steps[0].title}</h3>
                  <p className="text-xs text-gray-400 max-w-xs">{steps[0].description}</p>
                  <Button 
                    onClick={handleNextStep}
                    className="mt-4 bg-[#5D4BB7] hover:bg-[#4C3AA6] text-white py-1.5 px-5 rounded-full text-sm"
                  >
                    Let's go!
                  </Button>
                </div>
              )}

              {currentStep === 1 && (
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-light mb-3">{steps[1].title}</h3>
                  <div className="flex items-center space-x-2 mb-4">
                    <img src="/lovable-uploads/d87d4733-1fbd-45a7-9643-a2373aea4705.png" alt="Document" className="w-4 h-4" />
                    <span className="text-xs truncate">{steps[1].documentName}</span>
                  </div>
                  
                  <p className="text-xs text-gray-400 mb-2">Link document to:</p>
                  
                  <div className="space-y-3">
                    <button 
                      onClick={() => handleNextStep()}
                      className="w-full flex items-center justify-between p-2 rounded bg-[#0f1023] hover:bg-[#1a1b33] text-blue-400"
                    >
                      <span className="text-sm">Channel</span>
                      <ChevronRight size={16} />
                    </button>
                    
                    <button 
                      onClick={() => {
                        setCurrentStep(4);
                      }}
                      className="w-full flex items-center justify-between p-2 rounded bg-[#0f1023] hover:bg-[#1a1b33]"
                    >
                      <span className="text-sm">Solution</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-light mb-3">{steps[2].title}</h3>
                  
                  <div className="mb-4">
                    <button
                      onClick={() => setShowChannelDropdown(!showChannelDropdown)}
                      className="w-full flex items-center justify-between p-2 rounded bg-[#0f1023] hover:bg-[#1a1b33]"
                    >
                      <span className="text-sm">{selectedChannel ? getSelectedChannel()?.name : "Select a channel"}</span>
                      <ChevronDown size={16} />
                    </button>
                    
                    {showChannelDropdown && (
                      <div className="mt-1 bg-[#1a1b33] rounded overflow-hidden">
                        {channels.map(channel => (
                          <div
                            key={channel.id}
                            onClick={() => selectChannel(channel.id)}
                            className="flex items-center px-3 py-2 hover:bg-[#252642] cursor-pointer"
                          >
                            <img src={channel.logo} alt={channel.name} className="w-6 h-6 mr-2" />
                            <div className="flex-1">
                              <p className="text-sm">{channel.name}</p>
                            </div>
                            {channel.isNew && (
                              <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded">New</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-auto">
                    <Button 
                      onClick={handleNextStep}
                      className="w-full bg-[#5D4BB7] hover:bg-[#4C3AA6] text-white py-1.5 rounded-md text-sm"
                      disabled={!selectedChannel}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-light mb-3">{steps[3].title}</h3>
                  <div className="flex items-center space-x-2 mb-4">
                    <img src="/lovable-uploads/d87d4733-1fbd-45a7-9643-a2373aea4705.png" alt="Document" className="w-4 h-4" />
                    <span className="text-xs truncate">{steps[3].documentName}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 my-3 p-2 rounded bg-[#1a1b33]">
                    <img src={getSelectedChannel()?.logo} alt={getSelectedChannel()?.name} className="w-6 h-6" />
                    <span className="text-xs">{getSelectedChannel()?.name}</span>
                  </div>
                  
                  <p className="text-xs text-gray-400 mb-2">Link document to:</p>
                  
                  <div className="space-y-3">
                    <button 
                      onClick={() => handleNextStep()}
                      className="w-full flex items-center justify-between p-2 rounded bg-[#0f1023] hover:bg-[#1a1b33]"
                    >
                      <span className="text-sm">Solution</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-light mb-3">{steps[4].title}</h3>
                  
                  <div className="mb-4">
                    <button
                      onClick={() => setShowSolutionDropdown(!showSolutionDropdown)}
                      className="w-full flex items-center justify-between p-2 rounded bg-[#0f1023] hover:bg-[#1a1b33]"
                    >
                      <span className="text-sm">{selectedSolution ? getSelectedSolution()?.name : "Select a solution"}</span>
                      <ChevronDown size={16} />
                    </button>
                    
                    {showSolutionDropdown && (
                      <div className="mt-1 bg-[#1a1b33] rounded overflow-hidden">
                        {solutions.map(solution => (
                          <div
                            key={solution.id}
                            onClick={() => selectSolution(solution.id)}
                            className="flex items-center px-3 py-2 hover:bg-[#252642] cursor-pointer"
                          >
                            <div className="flex-1">
                              <p className="text-sm">{solution.name}</p>
                            </div>
                            <div className="flex items-center">
                              {solution.rating && (
                                <div className="flex mr-2">
                                  {[...Array(solution.rating)].map((_, i) => (
                                    <Star key={i} size={12} fill="#FFD700" stroke="#FFD700" className="mr-0.5" />
                                  ))}
                                </div>
                              )}
                              {solution.isNew && (
                                <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded">New</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-auto">
                    <Button 
                      onClick={handleNextStep}
                      className="w-full bg-[#5D4BB7] hover:bg-[#4C3AA6] text-white py-1.5 rounded-md text-sm"
                      disabled={!selectedSolution}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 5 && (
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-light mb-3">{steps[5].title}</h3>
                  <div className="flex items-center space-x-2 mb-4">
                    <img src="/lovable-uploads/d87d4733-1fbd-45a7-9643-a2373aea4705.png" alt="Document" className="w-4 h-4" />
                    <span className="text-xs truncate">{steps[5].documentName}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 my-3 p-2 rounded bg-[#1a1b33]">
                    <img src={getSelectedChannel()?.logo} alt={getSelectedChannel()?.name} className="w-6 h-6" />
                    <span className="text-xs">{getSelectedChannel()?.name}</span>
                  </div>
                  
                  <div className="p-2 my-3 rounded bg-[#1a1b33]">
                    <span className="text-xs">{getSelectedSolution()?.name}</span>
                  </div>
                  
                  <p className="text-xs text-gray-400 mb-2">Update frequency:</p>
                  
                  <div className="space-y-2 mb-5">
                    <button 
                      onClick={() => setSyncOption("realtime")}
                      className={`w-full flex items-center justify-between p-2 rounded ${syncOption === "realtime" ? "bg-[#5D4BB7]" : "bg-[#0f1023] hover:bg-[#1a1b33]"}`}
                    >
                      <span className="text-sm">Real-time</span>
                      {syncOption === "realtime" && <Check size={16} />}
                    </button>
                    
                    <button 
                      onClick={() => setSyncOption("daily")}
                      className={`w-full flex items-center justify-between p-2 rounded ${syncOption === "daily" ? "bg-[#5D4BB7]" : "bg-[#0f1023] hover:bg-[#1a1b33]"}`}
                    >
                      <span className="text-sm">Daily</span>
                      {syncOption === "daily" && <Check size={16} />}
                    </button>
                    
                    <button 
                      onClick={() => setSyncOption("weekly")}
                      className={`w-full flex items-center justify-between p-2 rounded ${syncOption === "weekly" ? "bg-[#5D4BB7]" : "bg-[#0f1023] hover:bg-[#1a1b33]"}`}
                    >
                      <span className="text-sm">Weekly</span>
                      {syncOption === "weekly" && <Check size={16} />}
                    </button>
                  </div>
                  
                  <div className="mt-auto">
                    <Button 
                      onClick={handleNextStep}
                      className="w-full bg-[#5D4BB7] hover:bg-[#4C3AA6] text-white py-1.5 rounded-md text-sm"
                    >
                      Link document
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 6 && (
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-light mb-3">{steps[6].title}</h3>
                  
                  <div className="bg-[#1a1b33] rounded p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm truncate">{steps[6].documentName}</span>
                      <Info size={14} className="text-gray-400" />
                    </div>
                    
                    <div className="flex items-center space-x-2 mb-2">
                      <img src={getSelectedChannel()?.logo} alt={getSelectedChannel()?.name} className="w-5 h-5" />
                      <span className="text-xs text-gray-300">{getSelectedChannel()?.name}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-400">
                        {syncOption === "realtime" ? "Real-time updates" : 
                         syncOption === "daily" ? "Daily updates" : "Weekly updates"}
                      </span>
                      <span className="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded">Live</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mt-2">
                    <button className="w-full flex items-center justify-between p-2 rounded bg-[#0f1023] hover:bg-[#1a1b33]">
                      <div className="flex items-center">
                        <CloudUpload size={16} className="mr-2" />
                        <span className="text-sm">Upload file</span>
                      </div>
                      <ChevronRight size={16} />
                    </button>
                    
                    <button className="w-full flex items-center justify-between p-2 rounded bg-[#0f1023] hover:bg-[#1a1b33]">
                      <div className="flex items-center">
                        <Link size={16} className="mr-2" />
                        <span className="text-sm">Add URL</span>
                      </div>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                  
                  <div className="mt-auto pt-6">
                    <Button 
                      onClick={handleNextStep}
                      variant="outline"
                      className="w-full border-gray-600 text-gray-300 hover:bg-[#1a1b33] hover:text-white py-1.5 rounded-md text-sm"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 7 && (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-3">
                  <div className="bg-green-600 rounded-full p-3 mb-2">
                    <Check size={30} />
                  </div>
                  <h3 className="text-lg font-light leading-tight">{steps[7].title}</h3>
                  <p className="text-xs text-gray-400 max-w-xs">{steps[7].description}</p>
                  <p className="text-xs text-blue-400">Your Giblet™ is learning from this content</p>
                  <Button 
                    onClick={() => setCurrentStep(0)}
                    className="mt-4 bg-[#5D4BB7] hover:bg-[#4C3AA6] text-white py-1.5 px-5 rounded-full text-sm"
                  >
                    Done
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IspaiConnectDemo;
